import { useState, useEffect } from 'react';
import { publicAdmissionsApi, type AdmissionsOpen } from '../lib/publicAdmissionsApi';
import { formatDate } from '../lib/dates';

/**
 * The public admissions ENQUIRY FORM — a prospective parent submits their interest straight from the
 * school's public page, and it lands as an Enquiry in the school's existing admissions pipeline
 * (Source = Website). No login. The form only renders if the school has its public admissions form
 * enabled.
 *
 * Colours are passed in from the public site so the form matches the school's brand.
 */
export function AdmissionsEnquiryForm({ brand, accent, onClose }: { brand: string; accent: string; onClose: () => void }) {
  const [status, setStatus] = useState<'checking' | 'closed' | 'form' | 'done'>('checking');
  const [config, setConfig] = useState<AdmissionsOpen | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // fields
  const [applicantName, setApplicantName] = useState('');
  const [seekingClass, setSeekingClass] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianMobile, setGuardianMobile] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [previousSchool, setPreviousSchool] = useState('');
  const [notes, setNotes] = useState('');

  /**
   * ⚠ WHICH SCHOOL OR COLLEGE THIS ENQUIRY IS FOR.
   *
   * Preselected when the trust runs exactly one institution — which is most of them — so nothing
   * changes for a single school. With several, the parent is asked, because the alternative was the
   * server picking one by database row order and the other institution's counsellor never seeing
   * the enquiry at all.
   */
  const [institutionId, setInstitutionId] = useState<string>('');

  useEffect(() => {
    publicAdmissionsApi.open()
      .then((c) => {
        setConfig(c);
        // ⚠ Array.isArray, not a truthiness check: an older server answers without `institutions`
        // at all, and `undefined.length` would take the whole form down rather than degrading.
        const list = Array.isArray(c.institutions) ? c.institutions : [];
        if (list.length === 1) setInstitutionId(list[0].id);
        else if (list.length === 0 && c.institutionId) setInstitutionId(c.institutionId);
        setStatus(c.open && (list.length > 0 || c.institutionId) ? 'form' : 'closed');
      })
      .catch(() => setStatus('closed'));
  }, []);

  const choices = Array.isArray(config?.institutions) ? config!.institutions! : [];
  const mustChoose = choices.length > 1;

  const submit = async () => {
    if (!institutionId) {
      setError('Please choose which school or college you are enquiring about.');
      return;
    }
    if (!applicantName.trim() || !seekingClass.trim() || !guardianName.trim() || !guardianMobile.trim()) {
      setError('Please fill the applicant name, class, and your name and mobile.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const res = await publicAdmissionsApi.submit({
        institutionId,
        applicantName: applicantName.trim(),
        seekingClass: seekingClass.trim(),
        guardianName: guardianName.trim(),
        guardianMobile: guardianMobile.trim(),
        guardianEmail: guardianEmail.trim() || null,
        dateOfBirth: dateOfBirth || null,
        gender: gender || null,
        previousSchool: previousSchool.trim() || null,
        notes: notes.trim() || null,
      });
      setReference(res.reference);
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 100,
      display: 'grid', placeItems: 'center', padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '90vh',
        overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)',
      }}>
        {/* header */}
        <div style={{ background: brand, color: '#fff', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>Admissions enquiry</div>
            {/* ⚠ Only when there is ONE — naming a single institution in the header of a form that
                covers three would be worse than naming none. */}
            {!mustChoose && config?.institutionName && <div style={{ fontSize: 13, opacity: .9 }}>{config.institutionName}</div>}
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 24 }}>
          {status === 'checking' && <p style={{ color: '#6b7280' }}>Loading…</p>}

          {status === 'closed' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 40 }}>📩</div>
              <h3 style={{ color: brand, margin: '10px 0' }}>Enquiries aren't open online right now</h3>
              <p style={{ color: '#6b7280', fontSize: 14 }}>Please contact the school directly — the details are on this page.</p>
              <button onClick={onClose} style={btn(accent)}>Close</button>
            </div>
          )}

          {status === 'done' && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 44 }}>✅</div>
              <h3 style={{ color: brand, margin: '10px 0' }}>Thank you!</h3>
              <p style={{ color: '#374151', fontSize: 15 }}>The school will contact you shortly.</p>
              {reference && (
                <p style={{ color: '#6b7280', fontSize: 13, marginTop: 8 }}>
                  Your reference: <strong style={{ color: brand, fontFamily: 'monospace' }}>{reference}</strong>
                </p>
              )}
              <button onClick={onClose} style={btn(accent)}>Done</button>
            </div>
          )}

          {status === 'form' && (
            <>
              {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>}

              {/*
                ⚠ FIRST FIELD, AND ONLY WHEN THERE IS A CHOICE. A trust running a school and a PU
                college has to know which one an enquiry is for — before this, the server chose by
                database row order and one institution's counsellor never saw the enquiry at all.
                A single-institution school sees no control: nothing to ask.
              */}
              {mustChoose && (
                <Group label="Which school or college? *">
                  <select style={inp} value={institutionId} onChange={(e) => setInstitutionId(e.target.value)}>
                    <option value="">Please choose…</option>
                    {choices.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </Group>
              )}

              <Group label="Student's name *"><input style={inp} value={applicantName} onChange={(e) => setApplicantName(e.target.value)} /></Group>
              <div style={{ display: 'flex', gap: 12 }}>
                <Group label="Class seeking *"><input style={inp} value={seekingClass} onChange={(e) => setSeekingClass(e.target.value)} placeholder="e.g. Class 6" /></Group>
                <Group label="Date of birth">
                  <input style={inp} type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
                  {/*
                    ⚠ THE DATE IS ECHOED BACK IN WORDS, AND THIS IS NOT DECORATION.

                    A native <input type="date"> renders its format in the BROWSER's locale, not the
                    page's — an en-US Windows machine shows `mm/dd/yyyy` on an Indian school's
                    website, and there is no attribute that changes it. A parent glancing at it and
                    typing 04/02/2012 meaning 4 February files a child born on 2 April.

                    Nothing errors. The value is a valid date. It flows into the admission, then the
                    student record, then a transfer certificate, and is discovered years later by a
                    clerk comparing it against a birth certificate.

                    A written month cannot be misread, so the parsed date is shown back underneath.
                    An error that was invisible becomes one the person who made it can see.
                  */}
                  {dateOfBirth && (
                    <div style={{ fontSize: 12.5, color: '#4b5563', marginTop: 4 }}>
                      {formatDate(dateOfBirth)}
                    </div>
                  )}
                </Group>
              </div>
              <Group label="Gender">
                <select style={inp} value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </Group>

              <div style={{ borderTop: '1px solid #eee', margin: '8px 0 16px' }} />

              <Group label="Your name (parent/guardian) *"><input style={inp} value={guardianName} onChange={(e) => setGuardianName(e.target.value)} /></Group>
              <div style={{ display: 'flex', gap: 12 }}>
                <Group label="Mobile *"><input style={inp} value={guardianMobile} onChange={(e) => setGuardianMobile(e.target.value)} inputMode="tel" /></Group>
                <Group label="Email"><input style={inp} value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)} inputMode="email" /></Group>
              </div>
              <Group label="Previous school"><input style={inp} value={previousSchool} onChange={(e) => setPreviousSchool(e.target.value)} /></Group>
              <Group label="Anything you'd like to add"><textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></Group>

              <button onClick={submit} disabled={busy} style={{ ...btn(accent), width: '100%', marginTop: 6 }}>
                {busy ? 'Submitting…' : 'Submit enquiry'}
              </button>
              <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 10 }}>
                The school will receive your enquiry and contact you. Your details are shared only with this school.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8,
  fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
};
function btn(accent: string): React.CSSProperties {
  return { marginTop: 16, padding: '11px 22px', background: accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' };
}
function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14, flex: 1 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}
